(function () {
  function initExpressionTracker() {
    var root = document.querySelector('[data-self-reflection]');
    if (!root || root.dataset.expressionTracker === 'true') return;
    root.dataset.expressionTracker = 'true';

    var video = root.querySelector('[data-sr-video]');
    var tracker = root.querySelector('[data-sr-tracker]');
    var output = root.querySelector('[data-sr-expression]');
    var status = root.querySelector('[data-sr-status]');
    var photoButton = root.querySelector('[data-sr-photo]');
    var recordButton = root.querySelector('[data-sr-record]');
    var analysisPanel = root.querySelector('[data-sr-analysis]');
    var analysisIndex = root.querySelector('[data-sr-analysis-index]');
    var analysisGroup = root.querySelector('[data-sr-analysis-group]');
    var analysisQuestion = root.querySelector('[data-sr-analysis-question]');
    var analysisPeople = root.querySelector('[data-sr-analysis-people]');
    if (!video || !tracker || !output) return;

    var ctx = tracker.getContext('2d');
    var modelsLoaded = false;
    var loading = false;
    var timer = null;
    var drawFrame = null;
    var currentPeople = [];
    var nextPersonId = 1;
    var lastAnalysis = null;
    var frozen = false;

    var expressionColors = {
      neutral: '#ffffff',
      happy: '#39ff14',
      angry: '#ff2b1c',
      sad: '#2f6bff',
      surprised: '#ffe600',
      fearful: '#b84dff',
      disgusted: '#ff8a00'
    };

    var reflectionQuestions = {
      neutral: 'HOW ARE YOU FEELING TODAY?',
      happy: 'WHAT IS MAKING YOU FEEL GOOD TODAY?',
      angry: 'WHAT IS FRUSTRATING YOU RIGHT NOW?',
      sad: 'ARE YOU OKAY TODAY?',
      surprised: 'WHAT CAUGHT YOU OFF GUARD?',
      fearful: 'WHAT ARE YOU WORRIED ABOUT?',
      disgusted: 'WHAT DOES NOT FEEL RIGHT TO YOU?'
    };

    function resizeTracker() {
      var rect = tracker.getBoundingClientRect();
      var ratio = window.devicePixelRatio || 1;
      tracker.width = Math.max(1, Math.round(rect.width * ratio));
      tracker.height = Math.max(1, Math.round(rect.height * ratio));
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    async function loadModels() {
      if (modelsLoaded || loading || !window.faceapi) return modelsLoaded;
      loading = true;
      try {
        var path = root.dataset.modelPath;
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(path),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(path),
          faceapi.nets.faceExpressionNet.loadFromUri(path)
        ]);
        modelsLoaded = true;
        if (status) status.textContent = 'LOOK INTO THE CAMERA.';
      } catch (error) {
        console.error('Expression model failed to load:', error);
        if (status) status.textContent = 'EXPRESSION READER COULD NOT LOAD.';
      } finally {
        loading = false;
      }
      return modelsLoaded;
    }

    function displayMetrics() {
      var displayWidth = tracker.clientWidth;
      var displayHeight = tracker.clientHeight;
      var sourceWidth = video.videoWidth;
      var sourceHeight = video.videoHeight;
      if (!displayWidth || !displayHeight || !sourceWidth || !sourceHeight) return null;
      var scale = Math.max(displayWidth / sourceWidth, displayHeight / sourceHeight);
      return {
        scale: scale,
        offsetX: (displayWidth - sourceWidth * scale) / 2,
        offsetY: (displayHeight - sourceHeight * scale) / 2,
        sourceWidth: sourceWidth
      };
    }

    function mapX(x, metrics) {
      return metrics.offsetX + (metrics.sourceWidth - x) * metrics.scale;
    }

    function mapY(y, metrics) {
      return metrics.offsetY + y * metrics.scale;
    }

    function buildRect(detection) {
      var metrics = displayMetrics();
      if (!metrics) return null;
      var box = detection.detection.box;
      var rawLeft = box.x + box.width * 0.04;
      var rawRight = box.x + box.width * 0.96;
      var rawTop = box.y - box.height * 0.03;
      var rawBottom = box.y + box.height * 1.02;
      var xA = mapX(rawLeft, metrics);
      var xB = mapX(rawRight, metrics);
      return {
        left: Math.min(xA, xB),
        right: Math.max(xA, xB),
        top: mapY(rawTop, metrics),
        bottom: mapY(rawBottom, metrics)
      };
    }

    function centerOf(rect) {
      return { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
    }

    function distance(a, b) {
      var dx = a.x - b.x;
      var dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function rankExpressions(values) {
      var ranked = Object.keys(values || {})
        .map(function (name) { return { name: name, score: values[name] || 0 }; })
        .sort(function (a, b) { return b.score - a.score; })
        .filter(function (item) { return item.score > 0; })
        .slice(0, 2);

      if (!ranked.length) {
        return [{ name: 'neutral', score: 1, confidence: 100 }];
      }

      if (ranked.length === 1) {
        ranked[0].confidence = 100;
        return ranked;
      }

      var total = ranked[0].score + ranked[1].score;
      if (!total) {
        ranked[0].confidence = 100;
        return ranked.slice(0, 1);
      }

      var first = Math.round((ranked[0].score / total) * 100);
      first = Math.max(1, Math.min(99, first));
      ranked[0].confidence = first;
      ranked[1].confidence = 100 - first;
      return ranked.filter(function (item) { return item.confidence > 0; });
    }

    function clonePeople(people) {
      return people.map(function (person) {
        return {
          id: person.id,
          dominant: {
            name: person.dominant.name,
            score: person.dominant.score,
            confidence: person.dominant.confidence
          },
          ranked: person.ranked.map(function (item) {
            return { name: item.name, score: item.score, confidence: item.confidence };
          })
        };
      });
    }

    function buildAnalysisFromPeople(people) {
      if (!people.length) return null;
      var snapshot = clonePeople(people);
      var dominants = snapshot.map(function (person) { return person.dominant.name; });
      var allSame = dominants.every(function (name) { return name === dominants[0]; });
      var groupLabel = allSame
        ? 'SHARED DOMINANT EXPRESSION: ' + dominants[0].toUpperCase()
        : 'MIXED DOMINANT EXPRESSIONS';
      var averageConfidence = snapshot.reduce(function (sum, person) {
        return sum + (person.dominant.confidence || 0);
      }, 0) / snapshot.length;
      var reflectionIndex = Math.round((averageConfidence * 0.7) + (allSame ? 30 : 0));

      return {
        people: snapshot.map(function (person) {
          return {
            id: person.id,
            top: person.ranked.map(function (item) {
              return { name: item.name, confidence: item.confidence };
            })
          };
        }),
        groupLabel: groupLabel,
        index: reflectionIndex,
        question: reflectionQuestions[allSame ? dominants[0] : 'neutral']
      };
    }

    function matchPeople(detections) {
      var now = Date.now();
      var candidates = detections.map(function (detection) {
        var rect = buildRect(detection);
        var ranked = rankExpressions(detection.expressions);
        return {
          rect: rect,
          center: rect ? centerOf(rect) : { x: 0, y: 0 },
          ranked: ranked,
          dominant: ranked[0] || { name: 'neutral', score: 1, confidence: 100 },
          matched: false
        };
      }).filter(function (candidate) { return candidate.rect; });

      currentPeople.forEach(function (person) { person.matched = false; });

      candidates.forEach(function (candidate) {
        var best = null;
        var bestDistance = Infinity;
        currentPeople.forEach(function (person) {
          if (person.matched) return;
          var d = distance(candidate.center, centerOf(person.targetRect));
          if (d < bestDistance && d < 180) {
            best = person;
            bestDistance = d;
          }
        });

        if (!best) {
          best = {
            id: nextPersonId++,
            targetRect: candidate.rect,
            currentRect: Object.assign({}, candidate.rect),
            matched: true,
            lastSeenAt: now,
            ranked: candidate.ranked,
            dominant: candidate.dominant
          };
          currentPeople.push(best);
        } else {
          best.targetRect = candidate.rect;
          best.matched = true;
          best.lastSeenAt = now;
          best.ranked = candidate.ranked;
          best.dominant = candidate.dominant;
        }
      });

      currentPeople = currentPeople.filter(function (person) {
        return now - person.lastSeenAt < 650;
      }).sort(function (a, b) { return a.id - b.id; });

      updateAnalysis();
    }

    function updateAnalysis() {
      if (!currentPeople.length) {
        output.textContent = 'NO FACE DETECTED';
        if (!frozen) lastAnalysis = null;
        return;
      }

      if (!frozen) {
        lastAnalysis = buildAnalysisFromPeople(currentPeople);
      }

      output.textContent = currentPeople.length + (currentPeople.length === 1 ? ' PERSON DETECTED' : ' PEOPLE DETECTED');
    }

    function freezeAnalysis() {
      if (!currentPeople.length) return;
      lastAnalysis = buildAnalysisFromPeople(currentPeople);
      frozen = true;
    }

    function renderAnalysisPanel() {
      if (!lastAnalysis || !analysisPanel) return;
      analysisPanel.hidden = false;
      if (analysisIndex) analysisIndex.textContent = String(lastAnalysis.index).padStart(2, '0') + '/100';
      if (analysisGroup) analysisGroup.textContent = lastAnalysis.groupLabel;
      if (analysisQuestion) analysisQuestion.textContent = lastAnalysis.question;
      if (analysisPeople) {
        analysisPeople.innerHTML = '';
        lastAnalysis.people.forEach(function (person) {
          var row = document.createElement('div');
          row.className = 'self-reflection__analysis-person';
          var title = document.createElement('span');
          title.textContent = lastAnalysis.people.length > 1 ? 'PERSON ' + String(person.id).padStart(2, '0') : 'READING';
          var reading = document.createElement('span');
          reading.textContent = person.top.map(function (item) {
            return item.name.toUpperCase() + ' ' + item.confidence + '%';
          }).join(' / ');
          row.appendChild(title);
          row.appendChild(reading);
          analysisPeople.appendChild(row);
        });
      }
    }

    async function detect() {
      if (frozen || !video.srcObject || video.readyState < 2) return;
      if (!modelsLoaded) {
        var ready = await loadModels();
        if (!ready) return;
      }
      try {
        var detections = await faceapi
          .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 }))
          .withFaceLandmarks(true)
          .withFaceExpressions();
        matchPeople(detections || []);
      } catch (error) {
        console.error('Expression detection failed:', error);
      }
    }

    function lerp(a, b, amount) {
      return a + (b - a) * amount;
    }

    function drawLabel(person, left, top) {
      var color = expressionColors[person.dominant.name] || '#ffffff';
      var showPerson = currentPeople.length > 1;
      var lines = [];
      if (showPerson) lines.push('PERSON ' + String(person.id).padStart(2, '0'));
      person.ranked.forEach(function (item) {
        var percent = item.confidence;
        if (percent > 0) lines.push(item.name.toUpperCase() + ' ' + percent + '%');
      });
      if (!lines.length) return;

      ctx.font = '600 11px "Source Code Pro", "Courier New", monospace';
      ctx.textBaseline = 'middle';
      var lineHeight = 17;
      var padX = 10;
      var padY = 7;
      var labelWidth = 0;
      lines.forEach(function (line) {
        labelWidth = Math.max(labelWidth, Math.ceil(ctx.measureText(line).width));
      });
      labelWidth += padX * 2;
      var labelHeight = lines.length * lineHeight + padY * 2;
      var labelX = Math.max(0, Math.min(tracker.clientWidth - labelWidth, left));
      var labelY = top - labelHeight;
      if (labelY < 0) labelY = top + 4;

      ctx.save();
      ctx.fillStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
      ctx.fillRect(labelX, labelY, labelWidth, labelHeight);
      ctx.restore();

      ctx.save();
      ctx.fillStyle = '#000000';
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 2;
      lines.forEach(function (line, index) {
        ctx.fillText(line, labelX + padX, labelY + padY + lineHeight * index + lineHeight / 2);
      });
      ctx.restore();
    }

    function drawTracker() {
      var width = tracker.clientWidth;
      var height = tracker.clientHeight;
      ctx.clearRect(0, 0, width, height);

      currentPeople.forEach(function (person) {
        var target = person.targetRect;
        var current = person.currentRect;
        current.left = lerp(current.left, target.left, 0.22);
        current.right = lerp(current.right, target.right, 0.22);
        current.top = lerp(current.top, target.top, 0.22);
        current.bottom = lerp(current.bottom, target.bottom, 0.22);

        var left = current.left;
        var top = current.top;
        var rectWidth = current.right - current.left;
        var rectHeight = current.bottom - current.top;
        var color = expressionColors[person.dominant.name] || '#ffffff';

        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.4;
        ctx.lineJoin = 'miter';
        ctx.lineCap = 'square';
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
        ctx.strokeRect(left, top, rectWidth, rectHeight);
        ctx.restore();

        drawLabel(person, left, top);
      });

      drawFrame = requestAnimationFrame(drawTracker);
    }

    function start() {
      frozen = false;
      tracker.hidden = false;
      resizeTracker();
      clearInterval(timer);
      timer = setInterval(detect, 220);
      cancelAnimationFrame(drawFrame);
      drawTracker();
      detect();
    }

    function stop() {
      clearInterval(timer);
      cancelAnimationFrame(drawFrame);
      currentPeople = [];
      ctx.clearRect(0, 0, tracker.clientWidth, tracker.clientHeight);
      tracker.hidden = true;
    }

    root.addEventListener('sr:capture', function () {
      if (!frozen) freezeAnalysis();
      renderAnalysisPanel();
    });

    root.addEventListener('sr:retake', function () {
      frozen = false;
      if (analysisPanel) analysisPanel.hidden = true;
    });

    if (photoButton) photoButton.addEventListener('click', freezeAnalysis, true);
    if (recordButton) recordButton.addEventListener('click', function () {
      if ((recordButton.textContent || '').toUpperCase().indexOf('STOP') > -1) freezeAnalysis();
    }, true);

    video.addEventListener('play', start);
    video.addEventListener('loadeddata', start);
    video.addEventListener('emptied', stop);
    window.addEventListener('resize', resizeTracker);
    window.addEventListener('pagehide', stop);
    if (!video.paused && video.readyState >= 2) start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExpressionTracker);
  } else {
    initExpressionTracker();
  }
})();
